import React, { useState, useRef, useEffect } from 'react';
import { apiService } from '../services/apiService';

// VANSH CHANGES START - Added isMobile prop for responsive bottom sheet on mobile
const AuthModal = ({ onClose, onLoginSuccess }) => {
  // VANSH CHANGES END
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // VANSH CHANGES START - Detect screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // VANSH CHANGES END
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [otp, setOtp] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const otpInputs = useRef([]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSendOtp = async (e) => {
  e.preventDefault();
  setError('');
  
  if (!formData.name || !formData.email) {
    setError('Please enter your name and email address.');
    return;
  }
  if (!/\S+@\S+\.\S+/.test(formData.email)) {
    setError('Please enter a valid email address.');
    return;
  }

  setLoading(true);
  
  try {
    const response = await apiService.initiateLogin({
      name: formData.name,
      email: formData.email
    });

    console.log('Login response:', response);

    if (response.success) {
      // Show success message
      setError('');
      setCurrentStep(2);
    } else {
      setError(response.message || 'Failed to send OTP. Please try again.');
    }
  } catch (error) {
    console.error('Login initiation error:', error);
    setError('An unexpected error occurred. Please try again.');
  } finally {
    setLoading(false);
  }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    if (value && index < 3) {
      otpInputs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    const pasteDigits = pasteData.replace(/\D/g, '').slice(0, 4);
    
    const newOtp = [...otp];
    pasteDigits.split('').forEach((digit, index) => {
      if (index < 4) {
        newOtp[index] = digit;
      }
    });
    setOtp(newOtp);
    
    if (pasteDigits.length >= 4) {
      otpInputs.current[3].focus();
    }
  };

  const handleSubmitOtp = async (e) => {
    e.preventDefault();
    const otpValue = otp.join('');
    
    if (otpValue.length < 4) {
      setError('Please enter the full 4-digit OTP.');
      return;
    }

    setLoading(true);
    
    try {
      const response = await apiService.verifyOtp({
        email: formData.email,
        otp: otpValue
      });

      console.log('OTP verification response:', response);

      if (response.success) {
        // Save user data and token in sessionStorage instead of localStorage
        if (response.data && response.data.token) {
          sessionStorage.setItem('userToken', response.data.token);
          sessionStorage.setItem('userData', JSON.stringify(response.data.user));
          
          if (onLoginSuccess) {
            onLoginSuccess(response.data);
          }
          
          // Show success message before closing
          setError('');
          setTimeout(() => {
            onClose();
          }, 500);
        } else {
          setError('Login successful but user data missing. Please try again.');
        }
      } else {
        setError(response.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // VANSH CHANGES START - Mobile Bottom Sheet UI
  // Renders bottom sheet on mobile devices, modal on desktop
  // All logic remains identical - only UI/styling changed
  if (isMobile) {
    return (
      <>
        <div
          onClick={onClose}
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        />

        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Handle Bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 bg-gray-300 rounded-full" />
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            disabled={loading}
            className="absolute top-4 right-4 z-30 p-1.5 bg-gray-100 rounded-full text-text-secondary hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Step 1 */}
            {currentStep === 1 && (
              <div>
                <h2 className="text-2xl font-bold text-text-primary text-center mb-6">
                  Login or Sign Up
                </h2>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="auth-name" className="block text-xs font-semibold text-text-secondary mb-1 uppercase tracking-wider">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="auth-name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                      disabled={loading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent text-sm disabled:opacity-50"
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="auth-email" className="block text-xs font-semibold text-text-secondary mb-1 uppercase tracking-wider">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="auth-email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      disabled={loading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent text-sm disabled:opacity-50"
                      placeholder="jane.doe@example.com"
                    />
                  </div>
                  <button
                    onClick={handleSendOtp}
                    disabled={loading}
                    className="w-full bg-brand-accent text-text-dark-on-accent h-10 rounded-md font-semibold text-sm hover:bg-brand-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 */}
            {currentStep === 2 && (
              <div>
                <h2 className="text-2xl font-bold text-text-primary text-center mb-4">
                  Enter OTP
                </h2>
                <p className="text-sm text-text-secondary text-center mb-6">
                  An OTP has been sent to
                  <span className="font-semibold text-text-primary"> {formData.email}</span>
                </p>
                
                <div className="flex items-center justify-center gap-3 mb-6">
                  {[0, 1, 2, 3].map((index) => (
                    <input
                      key={index}
                      ref={(el) => (otpInputs.current[index] = el)}
                      type="text"
                      maxLength="1"
                      value={otp[index]}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={handlePaste}
                      disabled={loading}
                      className="otp-input w-12 h-14 text-center text-2xl font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent disabled:opacity-50"
                    />
                  ))}
                </div>

                <button
                  onClick={handleSubmitOtp}
                  disabled={loading}
                  className="w-full bg-brand-accent text-text-dark-on-accent h-10 rounded-md font-semibold text-sm hover:bg-brand-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Submit'}
                </button>
                
                <button
                  onClick={() => setCurrentStep(1)}
                  disabled={loading}
                  className="w-full text-center text-sm text-text-secondary hover:text-brand-accent mt-3 disabled:opacity-50"
                >
                  Go Back
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
  // VANSH CHANGES END - Mobile Bottom Sheet UI

  // VANSH CHANGES START - Desktop Modal (Original Code - No Changes to Logic)
  // This renders the original modal on desktop screens
  // All authentication logic, validation, and API calls remain unchanged
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden relative">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-3 right-3 z-30 p-1.5 bg-gray-100 rounded-full text-text-secondary hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-6 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Step 1: Login/Signup Form */}
        {currentStep === 1 && (
          <div className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-text-primary text-center mb-6">
              Login or Sign Up
            </h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="auth-name" className="block text-xs font-semibold text-text-secondary mb-1 uppercase tracking-wider">
                  Full Name
                </label>
                <input
                  type="text"
                  id="auth-name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent text-sm disabled:opacity-50"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label htmlFor="auth-email" className="block text-xs font-semibold text-text-secondary mb-1 uppercase tracking-wider">
                  Email Address
                </label>
                <input
                  type="email"
                  id="auth-email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent text-sm disabled:opacity-50"
                  placeholder="jane.doe@example.com"
                />
              </div>
              <button
                onClick={handleSendOtp}
                disabled={loading}
                className="w-full bg-brand-accent text-text-dark-on-accent h-10 rounded-md font-semibold text-sm hover:bg-brand-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: OTP Verification */}
        {currentStep === 2 && (
          <div className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-text-primary text-center mb-4">
              Enter OTP
            </h2>
            <p className="text-sm text-text-secondary text-center mb-6">
              An OTP has been sent to
              <span className="font-semibold text-text-primary"> {formData.email}</span>
            </p>
            
            <div className="flex items-center justify-center gap-3 mb-6">
              {[0, 1, 2, 3].map((index) => (
                <input
                  key={index}
                  ref={(el) => (otpInputs.current[index] = el)}
                  type="text"
                  maxLength="1"
                  value={otp[index]}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handlePaste}
                  disabled={loading}
                  className="otp-input w-12 h-14 text-center text-2xl font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent disabled:opacity-50"
                />
              ))}
            </div>

            <button
              onClick={handleSubmitOtp}
              disabled={loading}
              className="w-full bg-brand-accent text-text-dark-on-accent h-10 rounded-md font-semibold text-sm hover:bg-brand-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Submit'}
            </button>
            
            <button
              onClick={() => setCurrentStep(1)}
              disabled={loading}
              className="w-full text-center text-sm text-text-secondary hover:text-brand-accent mt-3 disabled:opacity-50"
            >
              Go Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
  // VANSH CHANGES END - Desktop Modal
};

export default AuthModal;